import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';

import { ApplicationInsightsService } from './application-insights.service';
import { Account } from '@accounts/models/account';
import { AccountType } from '@accounts/models/account-type';
import { AuthService } from '@auth/services/auth.service';
import { User } from '@core/models/user';
import { APP_STORE } from '@core/stores/app.store';
import { USER_STORE } from '@core/stores/user.store';
import { waitFor } from '@core/utils/signal-async';
import { ENVIRONMENT } from '@env/environment';
import { MODULES_STORE } from '@modules/stores/modules.store';
import { ClarityService } from '@shared/services/clarity.service';

@Injectable()
export class AppInitializerService {
  readonly #injector = inject(Injector);
  readonly #clarityService = inject(ClarityService);
  readonly #router = inject(Router);
  readonly #authService = inject(AuthService);
  readonly #appStore = inject(APP_STORE);
  readonly #modulesStore = inject(MODULES_STORE);
  readonly #userStore = inject(USER_STORE);

  async init(): Promise<void> {
    await this.#authService.init();

    if (window.location.pathname === '/invitation') {
      await this.#authService.changePassword('');
      return;
    }

    const [user] = await Promise.all([
      this.loadUserEnsured(),
      this.loadModulesEnsured()
    ]);

    this.#clarityService.init();
    this.initializeApplicationInsights();

    if (!user?.accounts?.length) {
      await this.#router.navigate(['/error'], {
        queryParams: {
          errorType: 'no-account'
        }
      });
      return;
    }

    const clientIdFromURL = this.getQueryParamByName('clientId');
    const selectedAccount = this.selectAccount(user, clientIdFromURL);

    if (!clientIdFromURL) {
      if (window.location.pathname === '/error') {
        await this.#router.navigate(['/']);
      }
      return;
    }

    if (selectedAccount.accountType === AccountType.SystemAdmin) {
      await this.#router.navigate(['/clients', clientIdFromURL, 'details']);
    } else if (!user.accounts.some(x => x.client?.id === clientIdFromURL)) {
      await this.#router.navigate(['/page-not-found']);
    }
  }

  // #region Private methods

  private selectAccount(user: User, clientId: string | null): Account {
    const account =
      user.accounts.find(x => x.client?.id === clientId) ||
      user.accounts.find(
        x => x.client?.id === user.userState?.lastSelectedClientId
      ) ||
      user.accounts[0];

    this.#appStore.selectAccount(account);

    return account;
  }

  private async loadUserEnsured(): Promise<User | null> {
    this.#userStore.loadUser();

    const state = await waitFor(
      this.#injector,
      () => ({
        loaded: this.#userStore.loaded(),
        data: this.#userStore.data(),
        error: this.#userStore.error()
      }),
      value => value.loaded === true
    );

    return state.data;
  }

  private async loadModulesEnsured(): Promise<void> {
    this.#modulesStore.loadModules();

    await waitFor(
      this.#injector,
      () => this.#modulesStore.loaded(),
      loaded => loaded === true
    );
  }

  private initializeApplicationInsights(): void {
    if (!ENVIRONMENT.appInsights.enabled) {
      return;
    }

    const userEmail = this.#authService.getUserEmail();
    const applicationInsightsService = this.#injector.get(
      ApplicationInsightsService
    );

    applicationInsightsService.setUserId(userEmail!);
  }

  private getQueryParamByName(name: string): string | null {
    const url = new URLSearchParams(window.location.search);
    return url.get(name);
  }

  // #endregion
}
